import type { Database } from '@shkola/db-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';

/**
 * «Цифрові згоди й форми»: an excursion, a trip, an event of a class. The homeroom teacher or the
 * office makes a form; a guardian answers «agree / do not agree» and signs with their name; the
 * staff of the class see who signed and remind the rest. A «no» of any guardian means no; with
 * «both parents» every guardian must agree. Students abroad are not asked.
 */
export type FormKind = Database['public']['Enums']['form_kind'];

export const FORM_KINDS: readonly FormKind[] = ['excursion', 'trip', 'event', 'other'];
export const FORM_EMOJI: Record<FormKind, string> = {
  excursion: '🏛️',
  trip: '🚌',
  event: '🎭',
  other: '📝',
};

export interface PermissionForm {
  id: string;
  schoolId: string;
  classId: string;
  kind: FormKind;
  title: string;
  details: string | null;
  place: string | null;
  startsOn: string;
  endsOn: string;
  /** «09:00» */
  atTime: string | null;
  cost: string | null;
  deadline: string;
  bothGuardians: boolean;
  notePrompt: string | null;
  createdAt: string;
}

export interface FormAnswer {
  formId: string;
  studentId: string;
  guardianId: string;
  agreed: boolean;
  note: string | null;
  signedName: string;
  answeredAt: string;
}

/** «waiting» — nobody answered; «partly» — one of both parents agreed; «abroad» — not asked. */
export type FormStatus = 'agreed' | 'refused' | 'waiting' | 'partly' | 'abroad';

/** The answer of a family for one child. */
export function familyStatus(
  answers: ReadonlyArray<Pick<FormAnswer, 'agreed'>>,
  bothGuardians: boolean,
  guardians: number,
): FormStatus {
  if (answers.some((a) => !a.agreed)) return 'refused';
  const agreed = answers.filter((a) => a.agreed).length;
  if (agreed === 0) return 'waiting';
  if (!bothGuardians || agreed >= guardians) return 'agreed';
  return 'partly';
}

export interface FormRosterRow {
  studentId: string;
  firstName: string;
  lastName: string;
  abroad: boolean;
  guardians: number;
  status: FormStatus;
  answers: Array<{
    guardian: string;
    agreed: boolean;
    note: string | null;
    signedName: string;
    at: string;
  }>;
}

/** «18 погодили · 1 відмова · 7 чекаємо» */
export function rosterSummary(rows: ReadonlyArray<Pick<FormRosterRow, 'status'>>) {
  const count = (s: FormStatus) => rows.filter((r) => r.status === s).length;
  return {
    agreed: count('agreed'),
    refused: count('refused'),
    waiting: count('waiting') + count('partly'),
    abroad: count('abroad'),
  };
}

/** The deadline has passed (by the school's date). */
export const formClosed = (form: Pick<PermissionForm, 'deadline'>, today: string) =>
  form.deadline < today;

type FormRow = Database['public']['Tables']['permission_forms']['Row'];

const toForm = (r: FormRow): PermissionForm => ({
  id: r.id,
  schoolId: r.school_id,
  classId: r.class_id,
  kind: r.kind,
  title: r.title,
  details: r.details,
  place: r.place,
  startsOn: r.starts_on,
  endsOn: r.ends_on,
  atTime: r.at_time?.slice(0, 5) ?? null,
  cost: r.cost,
  deadline: r.deadline,
  bothGuardians: r.both_guardians,
  notePrompt: r.note_prompt,
  createdAt: r.created_at,
});

/** The forms one may see: of my children's classes, of the classes I teach, all for the office. */
export function usePermissionForms(schoolId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['forms', 'list', schoolId],
    queryFn: async () =>
      must(
        await db
          .from('permission_forms')
          .select('*')
          .eq('school_id', schoolId)
          .order('starts_on', { ascending: false }),
      ).map(toForm),
  });
}

/** The answers of a child's family (every form). */
export function useFormAnswers(studentId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['forms', 'answers', studentId],
    enabled: studentId !== '',
    queryFn: async (): Promise<FormAnswer[]> =>
      must(
        await db
          .from('permission_answers')
          .select('*')
          .eq('student_member_id', studentId)
          .order('answered_at'),
      ).map((a) => ({
        formId: a.form_id,
        studentId: a.student_member_id,
        guardianId: a.guardian_member_id,
        agreed: a.agreed,
        note: a.note,
        signedName: a.signed_name,
        answeredAt: a.answered_at,
      })),
  });
}

/** How many guardians a child has: «both parents» needs everyone. */
export function useGuardianCount(studentId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['forms', 'guardians', studentId],
    enabled: studentId !== '',
    staleTime: 30 * 60_000,
    queryFn: async () =>
      must(
        await db
          .from('guardianships')
          .select('guardian_member_id')
          .eq('student_member_id', studentId)
          .eq('status', 'active'),
      ).length,
  });
}

/** The class list of a form for its staff. */
export function useFormRoster(form: PermissionForm | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['forms', 'roster', form?.id],
    enabled: form !== undefined,
    queryFn: async (): Promise<FormRosterRow[]> =>
      must(await db.rpc('permission_roster', { p_form: (form as PermissionForm).id })).map((r) => {
        const answers = r.answers as FormRosterRow['answers'];
        return {
          studentId: r.student_member_id,
          firstName: r.first_name,
          lastName: r.last_name,
          abroad: r.abroad,
          guardians: r.guardians,
          status: r.abroad
            ? 'abroad'
            : familyStatus(answers, (form as PermissionForm).bothGuardians, r.guardians),
          answers,
        };
      }),
  });
}

export interface NewForm {
  classId: string;
  kind: FormKind;
  title: string;
  details: string;
  place: string;
  startsOn: string;
  endsOn: string;
  atTime: string;
  cost: string;
  deadline: string;
  bothGuardians: boolean;
  notePrompt: string;
}

export function useFormActions(schoolId: string) {
  const db = useDb();
  const client = useQueryClient();
  const onSettled = () => void client.invalidateQueries({ queryKey: ['forms'] });
  return {
    create: useMutation({
      mutationFn: async (f: NewForm & { authorId: string }) =>
        must(
          await db.from('permission_forms').insert({
            school_id: schoolId,
            class_id: f.classId,
            kind: f.kind,
            title: f.title.trim(),
            details: f.details.trim() || null,
            place: f.place.trim() || null,
            starts_on: f.startsOn,
            ends_on: f.endsOn < f.startsOn ? f.startsOn : f.endsOn,
            at_time: f.atTime || null,
            cost: f.cost.trim() || null,
            deadline: f.deadline,
            both_guardians: f.bothGuardians,
            note_prompt: f.notePrompt.trim() || null,
            author_member_id: f.authorId,
          }),
        ),
      onSettled,
    }),
    remove: useMutation({
      mutationFn: async (id: string) =>
        must(await db.from('permission_forms').delete().eq('id', id)),
      onSettled,
    }),
    answer: useMutation({
      mutationFn: async (a: {
        formId: string;
        studentId: string;
        agreed: boolean;
        signedName: string;
        note?: string;
      }) =>
        must(
          await db.rpc('answer_permission', {
            p_form: a.formId,
            p_student: a.studentId,
            p_agreed: a.agreed,
            p_signed_name: a.signedName,
            ...(a.note?.trim() && { p_note: a.note.trim() }),
          }),
        ),
      onSettled,
    }),
    remind: useMutation({
      mutationFn: async (formId: string) =>
        must(await db.rpc('remind_permission', { p_form: formId })),
      onSettled,
    }),
  };
}

/** Why an answer did not go through, for the message under the form. */
export function formError(error: unknown): 'signature' | 'deadline' | 'other' {
  const message = (error as { message?: string } | null)?.message ?? '';
  if (/signature does not match/.test(message)) return 'signature';
  if (/deadline has passed/.test(message)) return 'deadline';
  return 'other';
}
